import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useConversationHistory } from '../hooks/useConversationHistory.ts'
import { useConversationalMode } from '../hooks/useConversationalMode.ts'
import { getMessageLimit, useMessageLimits } from '../hooks/useMessageLimits.ts'
import {
  analyzeWithLLM,
  fileTooLargeUserMessage,
  PROJECT_AUTOFILL_CHAT_MAX_ATTACHMENT_BYTES,
  type LLMAnalysisResult,
  type LLMServiceError
} from '../services/llmService.ts'
import { loadConversations } from '../utils/conversationStorage.ts'
import type { Message } from '../utils/conversationStorage.ts'
import { ACTION_ID_TO_AGENT, AGENT_NAMES, isProjectAutofillAgentId } from '../constants/agentNames.ts'
import { AGENT_COLLECTED_DATA } from '../constants/agentIntents.ts'
import {
  hasPendingImage,
  getPendingImage,
  setPendingImageFromBlob,
  getPendingImageAsFile,
  clearPendingImage,
  getPendingAttachmentMime
} from '../utils/agentImageStorage.ts'
import { compressImage } from '../utils/imageCompression.ts'

export interface ConversationalAgentContextValue {
  currentSession: ReturnType<typeof useConversationHistory>['currentSession']
  allSessions: ReturnType<typeof useConversationHistory>['allSessions']
  createSession: ReturnType<typeof useConversationHistory>['createSession']
  loadSession: ReturnType<typeof useConversationHistory>['loadSession']
  addMessage: ReturnType<typeof useConversationHistory>['addMessage']
  removeSession: ReturnType<typeof useConversationHistory>['removeSession']
  clearCurrentSession: ReturnType<typeof useConversationHistory>['clearCurrentSession']
  isConversationalModeActive: boolean
  activateConversationalMode: ReturnType<typeof useConversationalMode>['activate']
  deactivateConversationalMode: ReturnType<typeof useConversationalMode>['deactivate']
  canSendMessage: boolean
  remainingMessages: number
  hasReachedLimit: boolean
  limit: number
  sendMessage: (
    message: string,
    options?: { confirmExecution?: boolean; activeAgentOverride?: string }
  ) => Promise<LLMAnalysisResult | null>
  isProcessing: boolean
  userId: string | undefined
  userData: any
  activeAgent: string
  setActiveAgent: (agent: string) => void
  collectedData: Record<string, any> | null
  setCollectedData: (data: Record<string, any> | null) => void
  missingFields: string[]
  readyToExecute: boolean
  setMissingFields: (fields: string[]) => void
  setReadyToExecute: (v: boolean) => void
  hasPendingImage: boolean
  imageUploadAcknowledged: boolean
  setImageUploadAcknowledged: (v: boolean) => void
  storePendingImage: (file: File) => Promise<void>
  clearPendingImage: () => void
  pendingImagePreview: string | null
  pendingAttachmentMime: string | null
  documentPipelineLoopActive: boolean
}

const ConversationalAgentContext = createContext<ConversationalAgentContextValue | null>(null)

export function useConversationalAgent(): ConversationalAgentContextValue {
  const value = useContext(ConversationalAgentContext)
  if (!value) {
    throw new Error('useConversationalAgent must be used within ConversationalAgentProvider')
  }
  return value
}

export function useConversationalAgentOptional(): ConversationalAgentContextValue | null {
  return useContext(ConversationalAgentContext)
}

function pickHandoffUserIntentFromLlm(result: LLMAnalysisResult): string | undefined {
  const u =
    (result as any).data?.handoffContext?.userIntent ?? (result as any).handoffContext?.userIntent
  if (typeof u === 'string' && u.trim().length > 0) return u.trim()
  return undefined
}

interface ConversationalAgentProviderProps {
  children: React.ReactNode
}

export function ConversationalAgentProvider({ children }: ConversationalAgentProviderProps) {
  const location = useLocation()
  const userData = useSelector((state: any) => state.general?.userData)
  const isUserPremium = useSelector((state: any) => state.general?.isUserPremium ?? false)
  const userId = userData?.id

  const {
    currentSession,
    allSessions,
    createSession,
    loadSession,
    addMessage,
    removeSession,
    clearCurrentSession
  } = useConversationHistory(userId)

  const {
    isActive: isConversationalModeActive,
    activate: activateConversationalMode,
    deactivate: deactivateConversationalMode
  } = useConversationalMode()

  const { canSendMessage, remainingMessages, hasReachedLimit, limit } = useMessageLimits(
    currentSession,
    isUserPremium
  )

  const [isProcessing, setIsProcessing] = useState(false)
  const [activeAgent, setActiveAgent] = useState<string>(AGENT_NAMES.general)
  const [collectedData, setCollectedData] = useState<Record<string, any> | null>(null)
  const [missingFields, setMissingFields] = useState<string[]>([])
  const [readyToExecute, setReadyToExecute] = useState(false)
  const [imageUploadAcknowledged, setImageUploadAcknowledged] = useState(false)
  const [, forceImagePreviewUpdate] = useState(0)
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null)
  const [pendingImageObjectUrl, setPendingImageObjectUrl] = useState<string | null>(null)
  const [documentPipelineLoopActive, setDocumentPipelineLoopActive] = useState(false)
  const [projectAutofillHandoffUserIntent, setProjectAutofillHandoffUserIntent] = useState<
    string | null
  >(null)

  const sessionId = currentSession?.sessionId
  const draftStorageKey = userId && sessionId ? `project-autofill-draft-${userId}-${sessionId}` : null
  const agentStorageKey =
    userId && sessionId ? `${AGENT_NAMES.projectAutofillAgent}-${userId}-${sessionId}` : null

  useEffect(() => {
    setPendingImageFile(null)
    setPendingImageObjectUrl(null)
    setReadyToExecute(false)
    setCollectedData(null)
    setMissingFields([])
    setActiveAgent(AGENT_NAMES.general)
    setDocumentPipelineLoopActive(false)
    setProjectAutofillHandoffUserIntent(null)
  }, [sessionId])

  useEffect(() => {
    const pathname = location.pathname
    const fromConversation = (location.state as any)?.fromConversation === true
    const sessionIdFromUrl = new URLSearchParams(location.search).get('session')
    if (pathname !== '/dashboard' && !fromConversation && !sessionIdFromUrl) {
      clearCurrentSession()
    }
  }, [location.pathname, (location.state as any)?.fromConversation, location.search])

  useEffect(() => {
    setImageUploadAcknowledged(false)
  }, [sessionId])

  useEffect(() => {
    if (!pendingImageFile) {
      setPendingImageObjectUrl(null)
      return
    }
    const objectUrl = URL.createObjectURL(pendingImageFile)
    setPendingImageObjectUrl(objectUrl)
    return () => {
      URL.revokeObjectURL(objectUrl)
    }
  }, [pendingImageFile])

  useEffect(() => {
    if (!draftStorageKey) return
    if (activeAgent !== AGENT_NAMES.projectAutofillAgent) return
    const storedDraftId = localStorage.getItem(draftStorageKey)
    if (!storedDraftId) return
    setCollectedData((prev) => ({ ...(prev || {}), draftId: storedDraftId }))
  }, [activeAgent, draftStorageKey])

  useEffect(() => {
    if (!agentStorageKey || !userId || !sessionId) return
    let storedActiveAgent = localStorage.getItem(agentStorageKey)
    if (!storedActiveAgent) {
      const legacyKey = `project-autofill-agent-${userId}-${sessionId}`
      const legacy = localStorage.getItem(legacyKey)
      if (legacy && isProjectAutofillAgentId(legacy)) {
        localStorage.setItem(agentStorageKey, AGENT_NAMES.projectAutofillAgent)
        localStorage.removeItem(legacyKey)
        storedActiveAgent = AGENT_NAMES.projectAutofillAgent
      }
    }
    if (isProjectAutofillAgentId(storedActiveAgent)) {
      setActiveAgent(AGENT_NAMES.projectAutofillAgent)
    }
  }, [agentStorageKey, userId, sessionId])

  useEffect(() => {
    if (!agentStorageKey) return
    if (activeAgent === AGENT_NAMES.projectAutofillAgent) {
      localStorage.setItem(agentStorageKey, AGENT_NAMES.projectAutofillAgent)
    } else {
      localStorage.removeItem(agentStorageKey)
    }
  }, [activeAgent, agentStorageKey])

  const storePendingImage = useCallback(
    async (file: File) => {
      const isImage = file.type.startsWith('image/')
      const isVideo = file.type.startsWith('video/')
      const isPdf = file.type === 'application/pdf'
      const canUsePdf = activeAgent === AGENT_NAMES.projectAutofillAgent || collectedData?.intent === 'project_autofill'
      if (!isImage && !isVideo && !(isPdf && canUsePdf)) return
      if (isVideo && collectedData?.intent !== 'create_post') return
      setPendingImageFile(file)
      const blob = isImage
        ? await compressImage(file)
        : file
      await setPendingImageFromBlob(blob, sessionId, userId)
      forceImagePreviewUpdate((n) => n + 1)
    },
    [sessionId, userId, collectedData?.intent, activeAgent]
  )

  const pendingImage = hasPendingImage(sessionId, userId) || !!pendingImageFile

  const clearPendingImageFn = useCallback(() => {
    setPendingImageFile(null)
    clearPendingImage(sessionId, userId)
    setImageUploadAcknowledged(false)
    forceImagePreviewUpdate((n) => n + 1)
  }, [sessionId, userId])

  const sendMessage = useCallback(
    async (
      message: string,
      options?: { confirmExecution?: boolean; activeAgentOverride?: string }
    ): Promise<LLMAnalysisResult | null> => {
      const conversationsSnapshot = loadConversations(userId)
      const sessionForLimit = conversationsSnapshot.currentSessionId
        ? conversationsSnapshot.sessions.find((s) => s.sessionId === conversationsSnapshot.currentSessionId) ?? null
        : null
      const messageLimit = getMessageLimit(isUserPremium)
      const underLimit =
        !sessionForLimit || (sessionForLimit.messageCount ?? 0) < messageLimit
      if (!underLimit && !options?.confirmExecution) return null

      const conversations = loadConversations(userId)
      let session = conversations.currentSessionId
        ? conversations.sessions.find((s) => s.sessionId === conversations.currentSessionId) || null
        : null
      if (!session) {
        session = createSession()
      }

      const targetSessionId = session.sessionId

      const msgContent = options?.confirmExecution ? '' : message.trim()
      if (!msgContent && !options?.confirmExecution) return null

      const userMessage: Message = {
        id: `msg-${Date.now()}-user`,
        role: 'user',
        content: options?.confirmExecution ? 'Confirmar creación' : message,
        timestamp: new Date().toISOString()
      }

      addMessage(userMessage, targetSessionId)
      setIsProcessing(true)

      await new Promise((resolve) => setTimeout(resolve, 100))

      const agentForRequest = options?.activeAgentOverride ?? activeAgent

      try {
        const freshConversations = loadConversations(userId)
        const currentSessionForLLM =
          freshConversations.sessions.find((s) => s.sessionId === targetSessionId) || session

        const allMessages = currentSessionForLLM.messages || []
        const messagesWithoutCurrent =
          allMessages.length > 0 && allMessages[allMessages.length - 1].id === userMessage.id
            ? allMessages.slice(0, -1)
            : allMessages
        const previousMessages = messagesWithoutCurrent.slice(-14)
        const historyForLLM = previousMessages.map((m) => {
          let content = m.role === 'assistant' && m.llmRawResponse ? m.llmRawResponse : m.content
          if (m.role === 'assistant' && m.llmRawResponse) {
            try {
              const parsed = JSON.parse(m.llmRawResponse)
              if ('imagePreviewUrl' in parsed) {
                const { imagePreviewUrl: _, ...rest } = parsed
                content = JSON.stringify(rest)
              }
            } catch {
            }
          }
          return { role: m.role, content }
        })

        const payloadCollectedData =
          collectedData ?? AGENT_COLLECTED_DATA[agentForRequest]
        const hasStoredImage = hasPendingImage(targetSessionId, userId)
        const hasImageInMemory = !!pendingImageFile
        const imageUploaded = hasStoredImage || hasImageInMemory
        const doConfirm = options?.confirmExecution === true
        const payloadWithImage = { ...payloadCollectedData }
        if (imageUploaded && !doConfirm) {
          payloadWithImage.image_uploaded = true
          setImageUploadAcknowledged(true)
        }
        const isProjectAutofillFlow = agentForRequest === AGENT_NAMES.projectAutofillAgent
        let imageToSend: File | undefined
        if (doConfirm && imageUploaded) {
          imageToSend = pendingImageFile ?? getPendingImageAsFile(targetSessionId, userId) ?? undefined
        } else if (!doConfirm && imageUploaded && isProjectAutofillFlow) {
          imageToSend = pendingImageFile ?? getPendingImageAsFile(targetSessionId, userId) ?? undefined
        }

        const handoffForRequest =
          agentForRequest === AGENT_NAMES.projectAutofillAgent && projectAutofillHandoffUserIntent
            ? { handoffContext: { userIntent: projectAutofillHandoffUserIntent } }
            : {}

        const runAnalyze = (draftIdOverride?: string) =>
          analyzeWithLLM(msgContent || (doConfirm ? 'Confirmar' : message), historyForLLM, {
            activeAgent: agentForRequest,
            collectedData: payloadWithImage,
            draftId: draftIdOverride !== undefined ? draftIdOverride : payloadWithImage?.draftId,
            imageUploaded,
            readyToExecute: doConfirm,
            image: imageToSend,
            ...handoffForRequest
          })

        if (
          imageToSend &&
          isProjectAutofillFlow &&
          imageToSend.size > PROJECT_AUTOFILL_CHAT_MAX_ATTACHMENT_BYTES
        ) {
          addMessage({
            id: `msg-${Date.now()}-error`,
            role: 'assistant',
            content: fileTooLargeUserMessage(PROJECT_AUTOFILL_CHAT_MAX_ATTACHMENT_BYTES),
            timestamp: new Date().toISOString()
          })
          return null
        }

        if (imageToSend?.type === 'application/pdf' && isProjectAutofillFlow) {
          setDocumentPipelineLoopActive(true)
        }

        let llmResult: LLMAnalysisResult
        try {
          llmResult = await runAnalyze()
        } catch (err) {
          const serviceErr = err as LLMServiceError
          const isDraftNotFound =
            serviceErr?.code === 'DRAFT_NOT_FOUND' &&
            agentForRequest === AGENT_NAMES.projectAutofillAgent
          if (!isDraftNotFound) throw err
          if (draftStorageKey) localStorage.removeItem(draftStorageKey)
          setCollectedData((prev) => {
            const next = { ...(prev || {}) }
            delete (next as any).draftId
            return next
          })
          llmResult = await runAnalyze('')
        }

        if (doConfirm && imageUploaded) {
          setPendingImageFile(null)
          clearPendingImage(targetSessionId, userId)
          setImageUploadAcknowledged(false)
        }
        if (!doConfirm && imageUploaded && isProjectAutofillFlow) {
          setPendingImageFile(null)
          clearPendingImage(targetSessionId, userId)
          setImageUploadAcknowledged(false)
        }
        if ((llmResult as any).collectedData) {
          setCollectedData(prev => ({ ...prev, ...(llmResult as any).collectedData }))
        }
        if ((llmResult as any).draftId) {
          setCollectedData(prev => ({ ...prev, draftId: (llmResult as any).draftId }))
          if (draftStorageKey) {
            localStorage.setItem(draftStorageKey, String((llmResult as any).draftId))
          }
        }
        if (Array.isArray((llmResult as any).missingFields)) {
          setMissingFields((llmResult as any).missingFields)
        }
        const isCreatedPortfolio = (llmResult as any).data &&
          typeof (llmResult as any).data.id !== 'undefined' &&
          Array.isArray((llmResult as any).data.portfolio_images)
        const isCreatedPost = (llmResult as any).data &&
          typeof (llmResult as any).data.id !== 'undefined' &&
          ((llmResult as any).data.content != null ||
            Array.isArray((llmResult as any).data.images) ||
            Array.isArray((llmResult as any).data.video))
        if ((llmResult as any).readyToExecute !== undefined) {
          setReadyToExecute((llmResult as any).readyToExecute === true)
        } else if ((llmResult as any).delegate === 'general-agent' || isCreatedPortfolio || isCreatedPost) {
          setReadyToExecute(false)
        } else if (doConfirm && llmResult.response) {
          const successKeywords = ['listo', 'publicado', 'creado', 'listo!']
          if (successKeywords.some(kw => llmResult.response.toLowerCase().includes(kw))) {
            setReadyToExecute(false)
          }
        }
        if (llmResult.actionId && ACTION_ID_TO_AGENT[llmResult.actionId]) {
          const nextAgent = ACTION_ID_TO_AGENT[llmResult.actionId]
          if (nextAgent !== AGENT_NAMES.projectAutofillAgent) {
            setProjectAutofillHandoffUserIntent(null)
          }
          setActiveAgent(nextAgent)
          if (agentStorageKey && nextAgent === AGENT_NAMES.projectAutofillAgent) {
            localStorage.setItem(agentStorageKey, AGENT_NAMES.projectAutofillAgent)
          }
          if (!(llmResult as any).collectedData) {
            const defaultCollected = AGENT_COLLECTED_DATA[nextAgent] || null
            setCollectedData(defaultCollected)
          }
        }
        if ((llmResult as any).activeAgent === AGENT_NAMES.projectAutofillAgent) {
          setActiveAgent(AGENT_NAMES.projectAutofillAgent)
          if (agentStorageKey) {
            localStorage.setItem(agentStorageKey, AGENT_NAMES.projectAutofillAgent)
          }
          if ((llmResult as any).draftId && draftStorageKey) {
            localStorage.setItem(draftStorageKey, String((llmResult as any).draftId))
          }
        }
        if ((llmResult as any).delegate === 'general-agent') {
          setProjectAutofillHandoffUserIntent(null)
          setActiveAgent(AGENT_NAMES.general)
          setCollectedData(null)
          setMissingFields([])
          setReadyToExecute(false)
          setPendingImageFile(null)
          clearPendingImage(targetSessionId, userId)
          setImageUploadAcknowledged(false)
          if (draftStorageKey) {
            localStorage.removeItem(draftStorageKey)
          }
          if (agentStorageKey) {
            localStorage.removeItem(agentStorageKey)
          }
        }
        if (isCreatedPortfolio) {
          setCollectedData(null)
          setMissingFields([])
        }
        if (isCreatedPost) {
          setCollectedData(null)
          setMissingFields([])
          setPendingImageFile(null)
          clearPendingImage(targetSessionId, userId)
          setImageUploadAcknowledged(false)
        }

        if ((llmResult as any).delegate !== 'general-agent') {
          const incomingHandoff = pickHandoffUserIntentFromLlm(llmResult)
          if (incomingHandoff) setProjectAutofillHandoffUserIntent(incomingHandoff)
        }

        const llmRawResponseObj: any = {
          actionId: llmResult.actionId,
          activeAgent: (llmResult as any).activeAgent,
          confidence: llmResult.confidence,
          response: llmResult.response,
          guideMode: llmResult.guideMode || false
        }
        if (Array.isArray(llmResult.suggestedActions) && llmResult.suggestedActions.length > 0) {
          llmRawResponseObj.suggestedActions = llmResult.suggestedActions
        }
        if ((llmResult as any).draftId) {
          llmRawResponseObj.draftId = (llmResult as any).draftId
        }
        if ((llmResult as any).nextAction) {
          llmRawResponseObj.nextAction = (llmResult as any).nextAction
        }
        if ((llmResult as any).validation) {
          llmRawResponseObj.validation = (llmResult as any).validation
        }
        if ((llmResult as any).draft) {
          llmRawResponseObj.draft = (llmResult as any).draft
        }

        if ((llmResult as any).missingFields) {
          llmRawResponseObj.missingFields = (llmResult as any).missingFields
        }
        if ((llmResult as any).collectedData) {
          llmRawResponseObj.collectedData = (llmResult as any).collectedData
        }
        if ((llmResult as any).readyToExecute !== undefined) {
          llmRawResponseObj.readyToExecute = (llmResult as any).readyToExecute
        }
        if (
          (llmResult.actionId === 'guardar-portfolio-item' || llmResult.actionId === 'guardar-post') &&
          (llmResult as any).collectedData?.image_uploaded === true
        ) {
          const previewUrl = getPendingImage(targetSessionId, userId)
          if (previewUrl) llmRawResponseObj.imagePreviewUrl = previewUrl
        }

        if (llmResult.needsData && llmResult.data) {
          llmRawResponseObj.needsData = true
          llmRawResponseObj.data = llmResult.data
          llmRawResponseObj.total = llmResult.total || 0
          if ((llmResult as any).quotationType)
            llmRawResponseObj.quotationType = (llmResult as any).quotationType
          if ((llmResult as any).workVisibility)
            llmRawResponseObj.workVisibility = (llmResult as any).workVisibility
          if ((llmResult as any).isPublic !== undefined)
            llmRawResponseObj.isPublic = (llmResult as any).isPublic
          if (llmResult.noResultsMessage)
            llmRawResponseObj.noResultsMessage = llmResult.noResultsMessage
          if ((llmResult as any).searchTerm)
            llmRawResponseObj.searchTerm = (llmResult as any).searchTerm
          if ((llmResult as any).projectType)
            llmRawResponseObj.projectType = (llmResult as any).projectType
        }

        const createdPortfolio = isCreatedPortfolio
          ? {
              ...(llmResult as any).data,
              type:
                (llmResult as any).data?.type ??
                (collectedData?.type as 'producto' | 'servicio' | undefined)
            }
          : undefined

        let createdPost = isCreatedPost ? (llmResult as any).data : undefined
        if (!createdPost && (llmResult as any).delegate === 'general-agent' && doConfirm && payloadWithImage?.intent === 'create_post') {
          createdPost = {
            id: 0,
            content: payloadWithImage?.content ?? undefined
          }
        }

        const llmRawResponse = JSON.stringify(llmRawResponseObj)

        const assistantMessage: Message = {
          id: `msg-${Date.now()}-assistant`,
          role: 'assistant',
          content: llmResult.response,
          timestamp: new Date().toISOString(),
          actionId: llmResult.actionId || null,
          guideMode: llmResult.guideMode || false,
          llmRawResponse,
          ...(createdPortfolio && { createdPortfolio }),
          ...(createdPost && { createdPost }),
          ...(llmResult.needsData &&
            llmResult.data && {
              data: llmResult.data,
              dataTotal: llmResult.total || 0,
              needsData: true,
              quotationType: (llmResult as any).quotationType,
              workVisibility: (llmResult as any).workVisibility,
              isPublic: (llmResult as any).isPublic,
              noResultsMessage: llmResult.noResultsMessage
            })
        }

        addMessage(assistantMessage)
        return llmResult
      } catch (error) {
        const serviceErr = error as LLMServiceError
        let content =
          'Disculpá, hubo un problema procesando tu consulta. ¿Podrías intentar de nuevo?'
        if (
          serviceErr?.code === 'FILE_TOO_LARGE' &&
          agentForRequest === AGENT_NAMES.projectAutofillAgent
        ) {
          content =
            serviceErr.userMessage ||
            fileTooLargeUserMessage(serviceErr.maxBytes)
        }
        const errorMsg: Message = {
          id: `msg-${Date.now()}-error`,
          role: 'assistant',
          content,
          timestamp: new Date().toISOString()
        }
        addMessage(errorMsg)
        return null
      } finally {
        setDocumentPipelineLoopActive(false)
        setIsProcessing(false)
      }
    },
    [
      isUserPremium,
      createSession,
      addMessage,
      userId,
      collectedData,
      activeAgent,
      pendingImageFile,
      draftStorageKey,
      agentStorageKey,
      projectAutofillHandoffUserIntent
    ]
  )

  const value: ConversationalAgentContextValue = {
    currentSession,
    allSessions,
    createSession,
    loadSession,
    addMessage,
    removeSession,
    clearCurrentSession,
    isConversationalModeActive,
    activateConversationalMode,
    deactivateConversationalMode,
    canSendMessage,
    remainingMessages,
    hasReachedLimit,
    limit,
    sendMessage,
    isProcessing,
    userId,
    userData,
    activeAgent,
    setActiveAgent,
    collectedData,
    setCollectedData,
    missingFields,
    readyToExecute,
    setMissingFields,
    setReadyToExecute,
    hasPendingImage: pendingImage,
    imageUploadAcknowledged,
    setImageUploadAcknowledged,
    storePendingImage,
    clearPendingImage: clearPendingImageFn,
    pendingImagePreview: getPendingImage(sessionId, userId)
      ?? pendingImageObjectUrl,
    pendingAttachmentMime: getPendingAttachmentMime(pendingImageFile, sessionId, userId),
    documentPipelineLoopActive
  }

  return (
    <ConversationalAgentContext.Provider value={value}>
      {children}
    </ConversationalAgentContext.Provider>
  )
}
